import React, { useState, useEffect, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import AppRoutes from "./AppRoutes";
import { Layout } from "./components/Common/Pages/Layout";
import Login from "./components/Login/Login";
import { PersistGate } from "redux-persist/integration/react";
import { persistor, store } from "./redux/store";
import { Provider, useSelector } from "react-redux";
import CacheBuster from "react-cache-buster";
import NotFound from "./components/Common/Pages/404";
import { setRoles } from "./redux/actions";
import { jwtDecode } from "jwt-decode"; // Corrected import
import { LicenseInfo } from "@mui/x-license";
import RequestApprovalModal from "./components/UserManagement/RequestApproval/RequestApprovalModal";

const packageJson = require("../package.json");
const version = packageJson.version;

export default function App() {
  const isProduction = process.env.NODE_ENV === "production";
  const [token, setToken] = useState(null);
  const [rolesUser, setRolesUser] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [request, setRequest] = useState();

  LicenseInfo.setLicenseKey(
    "fab6b27a03c9c97c3a265765f9c70bafTz0xMDMxMTUsRT0xNzY0NTI0MzE2MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI="
  );

  const userState = useSelector((state) => state.user);

  useEffect(() => {
    const tokenString = localStorage.getItem("token");
    if (tokenString) {
      try {
        const decodedToken = jwtDecode(tokenString);
        setToken(decodedToken);

        if (decodedToken.role) {
          setRolesUser(
            Array.isArray(decodedToken.role)
              ? decodedToken.role
              : [decodedToken.role]
          ); // Ensure roles are an array

          store.dispatch(
            setRoles(
              Array.isArray(decodedToken.role)
                ? decodedToken.role
                : [decodedToken.role]
            )
          );
        }
      } catch (e) {
        console.error("Error parsing token from localStorage", e);
      }
    }
    setLoading(false); // Set loading to false once done
  }, []);

  useEffect(() => {
    const checkForPendingApproval = async () => {
      const shouldShowModal = localStorage.getItem("showRequestApproval");

      if (shouldShowModal === "true") {
        try {
          // Fetch pending requests
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/employee/GetPendingRequestsByUser`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${userState.token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) throw new Error("Failed to fetch pending requests");

          const data = await response.json();

          if (data && data.length > 0) {
            setRequest(data); // Assuming the first pending request
            setShowApprovalModal(true);
          }

          // Clear the flag from localStorage
          localStorage.removeItem("showRequestApproval");
        } catch (error) {
          console.error("Error fetching pending requests:", error);
        }
      }
    };

    checkForPendingApproval();
  }, [userState.token]);

  const isAuthorized = (requiredRole) => {
    if (!requiredRole) return true; // No role required
    return rolesUser.some((role) => requiredRole.includes(role)); // Ensure roles are properly checked
  };

  const handleApprove = async (requestId) => {
    console.log("Request ID:", requestId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/ApproveRequest/${requestId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${userState.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to approve request");

      setShowApprovalModal(false);
    } catch (error) {
      console.error("Error approving request:", error);
    }
  };

  const handleReject = async (requestId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/employee/RejectRequest/${requestId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${userState.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to reject request");

      setShowApprovalModal(false);
    } catch (error) {
      console.error("Error rejecting request:", error);
    }
  };

  // If loading state is active, show a spinner or placeholder
  if (loading) {
    return <div>Loading...</div>; // Replace with a spinner or placeholder component if desired
  }

  return (
    <Suspense fallback={<div>Loading app...</div>}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <CacheBuster
            currentVersion={version}
            isEnabled={isProduction} // Cache busting enabled in production
            isVerboseMode={false}
            metaFileDirectory={"."}
          >
            {token && token.exp * 1000 > Date.now() ? ( // Corrected token expiration check
              <Layout>
                <Routes>
                  {AppRoutes.map((route, index) => {
                    const { element, requiredRole, ...rest } = route;
                    return (
                      <Route
                        key={index}
                        {...rest}
                        element={
                          isAuthorized(requiredRole) ? element : <NotFound />
                        }
                      />
                    );
                  })}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
            ) : (
              <Login />
            )}
          </CacheBuster>
          <RequestApprovalModal
            open={showApprovalModal}
            onClose={() => setShowApprovalModal(false)}
            request={request}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        </PersistGate>
      </Provider>
    </Suspense>
  );
}
